import { CancelReason, RetentionOffer } from "../../../../services/cancellationSurvey";

/** The main reasons, in the order they're kept in; the page shuffles them, "other" always last. */
export const CANCEL_REASONS: CancelReason[] = [
  "price",
  "lowUsage",
  "missingFeature",
  "technical",
  "competitor",
  "temporary",
  "businessChange",
  "onboarding",
  "other",
];

/**
 * The follow-up question of each reason (texts under `pages.subscriptionCancel.detail.{reason}`).
 * `benefits` lists the PLUS benefits to pick from; `other` only asks for text in the first step.
 */
export type FollowUp =
  | { kind: "single" | "multiple"; options: string[]; withText?: boolean }
  | { kind: "benefits"; withText?: boolean };

export const FOLLOW_UPS: Partial<Record<CancelReason, FollowUp>> = {
  price: { kind: "single", options: ["upTo10", "from10to15", "annual", "notPrice"], withText: true },
  lowUsage: { kind: "benefits", withText: true },
  missingFeature: {
    kind: "multiple",
    options: ["store", "scheduling", "integrations", "analytics", "design", "other"],
    withText: true,
  },
  technical: {
    kind: "single",
    options: ["editor", "domain", "publishing", "payment", "performance", "other"],
    withText: true,
  },
  competitor: { kind: "single", options: ["linktree", "beacons", "ownSite", "instagram", "other"], withText: true },
  temporary: { kind: "single", options: ["yes", "maybe", "no"] },
  businessChange: { kind: "single", options: ["yes", "maybe", "no"] },
  onboarding: {
    kind: "multiple",
    options: ["findFeatures", "customDomain", "sections", "design", "other"],
    withText: true,
  },
};

/**
 * What to offer before cancelling, by reason. The discount only shows while the account can still
 * use it; "benefits" lists the PLUS benefits the Keepo doesn't use yet.
 */
export const RETENTION_OFFERS: Record<CancelReason, RetentionOffer[]> = {
  price: ["discount"],
  lowUsage: ["discount", "benefits"],
  missingFeature: ["chat"],
  technical: ["chat"],
  competitor: ["discount"],
  temporary: [],
  businessChange: [],
  onboarding: ["chat", "benefits"],
  other: [],
};

/** Shuffles the reasons for each user (so the first option isn't picked for being first), keeping "other" last. */
export const shuffleReasons = (reasons: CancelReason[]) => {
  const rest = reasons.filter(reason => reason !== "other");
  for (let i = rest.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }
  return reasons.includes("other") ? [...rest, "other" as const] : rest;
};
